import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import Select from 'react-select';
import apiClient from '../api/client';
import { THEME } from '../theme/themeColors';

const BRAND       = THEME.primary;
const BRAND_LIGHT = THEME.primaryLight;
const BRAND_DARK  = THEME.primaryHover;
const ON_BRAND    = THEME.onPrimary;

const labelStyle = {
  fontSize: '10px', fontWeight: 600, color: '#495057',
  textTransform: 'uppercase', letterSpacing: '0.3px',
  marginBottom: '3px', display: 'block'
};

const selectStyles = {
  control: (base) => ({
    ...base, minHeight: '28px', height: '28px', fontSize: '12px',
    borderColor: '#dee2e6', boxShadow: 'none',
    '&:hover': { borderColor: BRAND },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, padding: '0 2px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  option: (base, state) => ({
    ...base, fontSize: '12px',
    backgroundColor: state.isSelected ? BRAND : state.isFocused ? BRAND_LIGHT : 'white',
    color: state.isSelected ? ON_BRAND : '#333',
    '&:active': { backgroundColor: BRAND_LIGHT },
  }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

export default function AssignResourceModal({ ticket, domain, onClose }) {
  const [resources,         setResources]         = useState([]);
  const [selectedResource,  setSelectedResource]  = useState(null);
  const [history,           setHistory]           = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [assigning,         setAssigning]         = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [resResources, resHistory] = await Promise.all([
          apiClient.get(`/api/adminusers`),
          apiClient.post(`/api/tickets/assign-history`, { domain, ticketId: ticket.bgTicketId }),
        ]);
        setResources(resResources.data.map(u => ({ value: u.value, label: u.label })));
        setHistory(resHistory.data || []);
      } catch {
        toast.error('Failed to load assignment data');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ticket, domain]);

  const handleAssign = async () => {
    if (!selectedResource) { toast.error('Please select a resource'); return; }
    setAssigning(true);
    try {
      await apiClient.post(`/api/tickets/assign`, {
        domain, ticketId: ticket.bgTicketId, assignedTo: selectedResource.value,
      });
      toast.success('Resource assigned successfully');
      onClose();
    } catch {
      toast.error('Failed to assign resource');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <>
      <style>{`
        .arm-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:1050;display:flex;align-items:center;justify-content:center; }
        .arm-modal { background:#fff;border-radius:8px;width:min(520px,96vw);max-height:90vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,0.18); }
        .arm-header { background:${BRAND};padding:8px 14px;display:flex;align-items:center;justify-content:space-between;flex-shrink:0; }
        .arm-header h6 { margin:0;color:${ON_BRAND};font-size:13px;font-weight:700;letter-spacing:0.2px; }
        .arm-header .badge { font-size:11px;color:${ON_BRAND};background:rgba(255,255,255,0.22);padding:2px 10px;border-radius:20px;margin-left:8px; }
        .arm-close { background:none;border:none;color:${ON_BRAND};font-size:18px;line-height:1;cursor:pointer;padding:0 2px;opacity:0.85; }
        .arm-close:hover { opacity:1; }
        .arm-body { flex:1;overflow-y:auto;padding:12px 14px; }
        .arm-section { border:1px solid #e9ecef;border-radius:6px;margin-bottom:10px;overflow:hidden; }
        .arm-section-title { font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:${BRAND_DARK};background:${BRAND_LIGHT};padding:5px 10px;border-bottom:1px solid ${THEME.alpha(0.25)}; }
        .arm-section-body { padding:10px; }
        .arm-btn { height:28px;font-size:11px;font-weight:600;padding:0 16px;border-radius:4px;border:none;cursor:pointer;display:inline-flex;align-items:center;gap:5px; }
        .arm-btn-primary { background:${BRAND};color:${ON_BRAND}; }
        .arm-btn-primary:hover:not(:disabled) { background:${BRAND_DARK}; }
        .arm-btn-primary:disabled { background:#6c757d;color:#fff;cursor:not-allowed; }
        .arm-btn-ghost { background:none;border:none;color:#6c757d;font-size:11px;padding:0 8px;cursor:pointer;height:28px;border-radius:4px; }
        .arm-btn-ghost:hover { background:#f8f9fa; }
        .arm-table { width:100%;border-collapse:collapse;font-size:11px; }
        .arm-table th { background:#f8f9fa;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.3px;color:#495057;padding:5px 8px;border-bottom:1px solid #dee2e6;white-space:nowrap; }
        .arm-table td { padding:5px 8px;border-bottom:1px solid #f1f3f4;color:#212529;vertical-align:middle; }
        .arm-table tr:last-child td { border-bottom:none; }
        .arm-table tr:hover td { background:${BRAND_LIGHT}; }
        .arm-empty { text-align:center;padding:16px;font-size:12px;color:#adb5bd; }
        .arm-footer { padding:7px 14px;border-top:1px solid #e9ecef;display:flex;justify-content:flex-end;background:#fafafa;flex-shrink:0; }
        .arm-spinner { width:10px;height:10px;border:2px solid rgba(255,255,255,0.4);border-top-color:#fff;border-radius:50%;animation:arm-spin 0.7s linear infinite;display:inline-block; }
        .arm-spinner-dark { width:18px;height:18px;border:2px solid ${BRAND_LIGHT};border-top-color:${BRAND};border-radius:50%;animation:arm-spin 0.7s linear infinite;display:inline-block; }
        @keyframes arm-spin { to { transform:rotate(360deg); } }
      `}</style>

      <div className="arm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="arm-modal">

          {/* Header */}
          <div className="arm-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h6>Assign Resource</h6>
              {ticket?.szTicketNumber && <span className="badge">{ticket.szTicketNumber}</span>}
            </div>
            <button className="arm-close" onClick={onClose}>×</button>
          </div>

          <div className="arm-body">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '32px' }}>
                <div className="arm-spinner-dark" style={{ margin: '0 auto 8px' }} />
                <div style={{ fontSize: '12px', color: '#6c757d' }}>Loading...</div>
              </div>
            ) : (
              <>
                {/* Assign Section */}
                <div className="arm-section">
                  <div className="arm-section-title">Assign To</div>
                  <div className="arm-section-body">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', alignItems: 'end' }}>
                      <div>
                        <label style={labelStyle}>Select Resource</label>
                        <Select
                          options={resources}
                          value={selectedResource}
                          onChange={setSelectedResource}
                          placeholder="-- Select resource --"
                          styles={selectStyles}
                          classNamePrefix="react-select"
                          menuPortalTarget={document.body}
                        />
                      </div>
                      <button className="arm-btn arm-btn-primary" onClick={handleAssign} disabled={!selectedResource || assigning}>
                        {assigning ? <><span className="arm-spinner" />Assigning...</> : 'Assign'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* History Section */}
                <div className="arm-section">
                  <div className="arm-section-title">Assignment History</div>
                  <div className="arm-section-body" style={{ padding: '0' }}>
                    {history.length === 0 ? (
                      <div className="arm-empty">No assignment history yet.</div>
                    ) : (
                      <table className="arm-table">
                        <thead>
                          <tr>
                            <th>Created On</th>
                            <th>Created By</th>
                            <th>Assigned To</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.map((h, idx) => (
                            <tr key={idx}>
                              <td style={{ whiteSpace: 'nowrap' }}>{h.dtCreated || '—'}</td>
                              <td>{h.szCreatedBy || '—'}</td>
                              <td>{h.szAssignedTo || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="arm-footer">
            <button className="arm-btn arm-btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </>
  );
}